import { queryOptions, useQuery } from '@tanstack/react-query'
import type { TrackerFetcher } from '@/provider/TrackerProvider.types'
import { apiFetch } from './api'
import type { TargetMarketCatalog } from './targetMarkets.types'

export function findGlobalTargetMarket(catalog: TargetMarketCatalog) {
  return catalog.targetMarkets.find((market) => market.type === 'global')
}

export function findTargetMarketLabel(
  catalog: TargetMarketCatalog,
  ids: string[]
): string | null {
  const labels = ids
    .map(
      (id) => catalog.targetMarkets.find((market) => market.id === id)?.label
    )
    .filter((label): label is string => label !== undefined)

  return labels.length > 0 ? labels.join(', ') : null
}

export function targetMarketIdsEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    targetMarketIdsKey(left) === targetMarketIdsKey(right)
  )
}

export function targetMarketIdsKey(ids: string[]): string {
  return ids.slice().sort().join('|')
}

export function appendTargetMarketIds(
  params: URLSearchParams,
  ids: string[]
): void {
  ids.forEach((id) => {
    params.append('targetMarketIds', id)
  })
}

const TARGET_MARKETS_KEY = ['target-markets'] as const

export function targetMarketCatalogOptions(fetcher: TrackerFetcher) {
  return queryOptions({
    queryKey: TARGET_MARKETS_KEY,
    queryFn: async ({ signal }) => {
      const catalog = await apiFetch<TargetMarketCatalog>(
        fetcher,
        '/api/target-markets',
        { signal }
      )

      if (!catalog) {
        throw new Error('target-market catalog request returned an empty body')
      }

      return catalog
    },
    staleTime: 5 * 60_000,
  })
}

export function useTargetMarketCatalog(fetcher: TrackerFetcher) {
  return useQuery(targetMarketCatalogOptions(fetcher))
}
